<template>
  <Dialog
    v-model:show="dialogVisible"
    :close-on-click-modal="false"
    :height="600"
    title="上传企业知识文件"
    :width="800"
  >
    <div class="upload-knowledge">
      <DropUploadedFile
        v-model:file-list="displayFileList"
        :max-size="maxSize"
        multiple
        :show-file-list="false"
        @success="handleUploadSuccess"
      />
      <div class="upload-knowledge__uploaded-title">
        <span class="upload-knowledge__uploaded-title-bar"></span>
        <span class="upload-knowledge__uploaded-title-text">已上传文件</span>
      </div>
      <div v-if="!displayFileList.length" class="upload-knowledge__empty">
        <Empty empty-text="暂未上传企业知识文件" />
      </div>
      <div v-else class="upload-knowledge__file-list">
        <UploadedFileCard
          v-for="file in displayFileList"
          :key="file.id || file.uid"
          :file="file"
          :width="240"
          @delete="handleDeleteFile(file)"
        >
          <template #meta>
            <span v-if="file.addTime" class="upload-knowledge__file-meta">
              {{ xbb.formatTime(file.addTime, 'yyyy-MM-dd HH:mm') }} ·
              {{ file.creatorName }}
            </span>
          </template>
        </UploadedFileCard>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import Dialog from '@/components/layout/dialog-layout/index.vue'
import DropUploadedFile from '@/components/upload/DropUploadedFile.vue'
import Empty from '@/components/Empty/index.vue'
import UploadedFileCard from '@/components/upload/UploadedFileCard.vue'
import { computed, ref, watch } from 'vue'
import { ElMessageBox } from 'element-plus'
import { delCompanyKnowledge, getCompanyKnowledge, uploadCompanyKnowledge } from '@/api/ai-setting'
import xbb from '@xbb/xbb-utils'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  knowledgeType: {
    type: Number,
    default: null
  }
})
const emit = defineEmits(['update:show'])

const DEFAULT_MAX_SIZE = 20
const innerVisible = ref(false)
const knowledgeData = ref(null)

const dialogVisible = computed({
  get() {
    return innerVisible.value
  },
  set(value) {
    innerVisible.value = value
    if (!value) {
      emit('update:show', false)
    }
  }
})

const getKnowledgeData = async () => {
  const res = await getCompanyKnowledge({
    type: props.knowledgeType
  })
  knowledgeData.value = res.result
}

const openAfterKnowledgeLoaded = async () => {
  if (!props.knowledgeType) {
    emit('update:show', false)
    return
  }

  try {
    await getKnowledgeData()
    if (props.show) {
      innerVisible.value = true
    }
  } catch (error) {
    emit('update:show', false)
    console.error('获取企业知识文件失败', error)
  }
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      openAfterKnowledgeLoaded()
      return
    }

    innerVisible.value = false
  }
)

// 最大文件大小
const maxSize = computed(() => {
  if (!knowledgeData.value?.maxSize) {
    return DEFAULT_MAX_SIZE
  }
  return knowledgeData.value.maxSize / 1024 / 1024
})

const displayFileList = computed({
  get() {
    return knowledgeData.value?.fileList || []
  },
  set(fileList) {
    if (!knowledgeData.value) {
      knowledgeData.value = {}
    }
    knowledgeData.value.fileList = fileList
  }
})

const getFileKey = (file = {}) => file.attachIndex || file.id || file.uid

const removeDisplayFile = (file = {}) => {
  const fileKey = getFileKey(file)
  if (!fileKey) {
    return
  }
  displayFileList.value = displayFileList.value.filter((item) => getFileKey(item) !== fileKey)
}

const handleUploadSuccess = async (response, file) => {
  try {
    await uploadCompanyKnowledge({
      type: props.knowledgeType,
      fileList: [response]
    })
  } catch (error) {
    removeDisplayFile(file)
    console.error('上传企业知识文件失败', error)
    return
  }

  try {
    await getKnowledgeData()
  } catch (error) {
    console.error('刷新企业知识文件失败', error)
  }
}

// 删除文件
const handleDeleteFile = async (file) => {
  if (!file.id) {
    removeDisplayFile(file)
    return
  }

  try {
    await ElMessageBox.confirm(
      '删除后，该文件将不再用于当前功能后续生成的分析结果，是否确认删除？',
      '请确认是否删除',
      {
        cancelButtonText: '取消',
        confirmButtonText: '确定',
        type: 'warning'
      }
    )
  } catch {
    return
  }

  await delCompanyKnowledge({
    fileIdList: [file.id],
    type: props.knowledgeType
  })
  await getKnowledgeData()
}
</script>

<style lang="scss" scoped>
$upload-knowledge-title: #323233; // 文字/Text-4 强调正文标题
$upload-knowledge-title-bar: #7a52f4; // 拓展色/紫罗兰/Violets-6

.upload-knowledge__uploaded-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.upload-knowledge__uploaded-title-bar {
  width: 4px;
  height: 16px;
  border-radius: 4px;
  background: $upload-knowledge-title-bar;
}

.upload-knowledge__uploaded-title-text {
  color: $upload-knowledge-title;
  font-family: 'PingFang SC', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.upload-knowledge__empty {
  height: 320px;
  margin-top: 8px;
}

.upload-knowledge__file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.upload-knowledge__file-meta {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
